import React from "react";
import { motion } from "framer-motion";
import { Award } from "lucide-react";

const awards = [
    { year: "2022", title: "Lokmat Health Awards", note: "Excellence in Affordable Healthcare" },
    { year: "2022", title: "Zee Delhi Health & Wellness", note: "Outstanding Contribution to Public Health" },
    { year: "2022", title: "TheHealthSite Health Awards", note: "Most Ingenious Clinician of the Year" },
    { year: "2021", title: "Times — Global Indian Physician", note: "National recognition feature" },
    { year: "2021", title: "Central Railway, Govt. of India", note: "Official letter of appreciation" },
];

const press = [
    "Times of India", "Hindustan Times", "Bombay Times", "Loksatta",
    "Lokmat", "Zee News", "TheHealthSite", "Mid-Day",
];

const Awards = () => {
    return (
        <section
            id="awards"
            data-testid="awards-section"
            className="relative py-24 md:py-32 lg:py-40 bg-sage"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-8">
                    <div className="col-span-12 lg:col-span-4">
                        <div className="section-number">06 / Recognition</div>
                        <div className="mt-6 divider" />
                        <div className="mt-4 eyebrow">Awards & Press</div>
                    </div>

                    <div className="col-span-12 lg:col-span-8">
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink-900 max-w-3xl"
                        >
                            Honours <em className="text-bisleri">earned at the counter</em>,
                            not in the boardroom.
                        </motion.h2>
                    </div>
                </div>

                <div className="mt-16 md:mt-20">
                    {awards.map((a, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                            className="group grid grid-cols-12 gap-4 items-center py-7 md:py-8 border-t border-ink-900/10 last:border-b hover:bg-bisleri-light/40 px-3 -mx-3 transition-colors"
                            data-testid={`award-${i}`}
                        >
                            <div className="col-span-2 font-mono text-bisleri tracking-widest text-sm">
                                {a.year}
                            </div>
                            <div className="col-span-9 md:col-span-9">
                                <div className="font-display text-2xl md:text-3xl text-ink-900">
                                    {a.title}
                                </div>
                                <div className="mt-1 font-body text-sm md:text-base text-ink-600">
                                    {a.note}
                                </div>
                            </div>
                            <div className="col-span-1 text-right">
                                <Award className="inline text-ink-500 group-hover:text-clinicgreen-deep transition-colors" size={20} />
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Press marquee */}
                <div className="mt-20 md:mt-24">
                    <div className="eyebrow mb-6">As featured in</div>
                    <div className="overflow-hidden border-y border-ink-900/10 py-6 bg-bisleri-light/40">
                        <div className="flex gap-16 marquee-track whitespace-nowrap">
                            {[...press, ...press].map((p, i) => (
                                <span
                                    key={i}
                                    className="font-display italic text-3xl md:text-4xl text-ink-800 hover:text-bisleri transition-colors flex-shrink-0"
                                >
                                    {p}<span className="text-clinicgreen-deep not-italic mx-8">✦</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Partnerships */}
                <div className="mt-20 md:mt-24 grid grid-cols-12 gap-8">
                    <div className="col-span-12 lg:col-span-4">
                        <div className="eyebrow">Public Sector Trust</div>
                        <h3 className="mt-3 font-display text-3xl md:text-4xl text-ink-900">
                            Trusted Partners
                        </h3>
                    </div>
                    <div className="col-span-12 lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-3">
                        {[
                            "Central Railway", "Western Railway", "KDMC, Kalyan-Dombivli",
                            "PMC, Panvel", "MBMC, Mira-Bhayandar", "TMC, Thane",
                            "Vedanta Medical College", "Govt. of Maharashtra", "Indian Railways MoHFW",
                        ].map((p) => (
                            <div
                                key={p}
                                className="bg-paper-50 px-5 py-6 border border-ink-900/10 hover:border-bisleri/40 hover:bg-bisleri-light/30 transition-colors"
                                data-testid={`partner-${p.split(",")[0].toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <div className="font-display text-base md:text-lg text-ink-900 leading-tight">
                                    {p}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Awards;
